import { Stack } from '@chakra-ui/react';
import { Workshop } from 'api/types';
import { useUserContext } from 'context/UserContextProvider';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import Link from '../../components/Link';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { MultiSelectDropDown } from '../../components/Table/MultiSelectDropDown';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import useWorkshopUpdateRequestsQuery from '../../hooks/useWorkshopUpdateRequestsQuery';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopUpdateRequestOverviewCard = () => {
  const { t } = useTranslation();
  const { statusCodes } = useUserContext();
  const workshopUpdateRequestCodes = statusCodes?.workshop_update_request || {};

  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
    status: ['0'],
    assignee: undefined,
  };
  const localStorageData = window.localStorage.getItem('workshop_update_requests_filters');
  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('workshop_update_requests_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [assignee, setAssignee] = useState(destringifiedData?.assignee || initialFilters?.assignee);

  const [filtersSelected, setFiltersSelected] = useState(false);

  const workshopUpdateRequestsQuery = useWorkshopUpdateRequestsQuery({
    searchString,
    page,
    itemsPerPage,
    status,
    assignee,
  });

  const initialLoad = searchString === '' && itemsPerPage === 20 && page === 1 && status.length === 1 && status[0] === '0' && isNil(assignee);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, status]);

  useEffect(() => {
    setFiltersSelected(!isNil(assignee) || status?.length !== 0);

    window.localStorage.setItem(
      'workshop_update_requests_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        assignee: assignee,
        itemsPerPage: itemsPerPage,
      }),
    );
  }, [searchString, status, assignee, itemsPerPage]);

  const allStatusSelected = status.length === Object.keys(workshopUpdateRequestCodes).length;

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return <Link to={`/app/op/workshop-update-request/${row.original.id}`}>{row.original.user_readable_id}</Link>;
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        Filter: () =>
          MultiSelectDropDown(status, setStatus, 'workshop_update_request', workshopUpdateRequestCodes, allStatusSelected, t('forms:status.label')),
        // @ts-ignore
        Cell: ({ value }) => <StatusTag resourceKey={'workshop_update_request'} currentStatus={value} />,
      },
      {
        Header: t('forms:assignee.label'),
        accessor: 'open_task.assignee.name',
        // @ts-ignore
        Cell: (props) => {
          return props.value ?? '-';
        },
      },
      {
        Header: t('forms:date.label'),
        accessor: (row: { processed_at: string }) => dateTimeTransformer(row.processed_at) ?? '-',
      },
      {
        Header: t('workshop:name.label'),
        accessor: (row: { workshop: Workshop }) => row.workshop?.name,
      },
      {
        Header: t('workshop:email.label'),
        accessor: (row: { workshop: Workshop }) => row.workshop?.email,
      },
      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link to={`/app/op/workshop-update-request/${row.original.id}`} data-test-id={'edit-' + row.index}>
              {t('common:edit')}
            </Link>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workshopUpdateRequestCodes, allStatusSelected, searchString, status, assignee, itemsPerPage],
  );

  return (
    <ContentCard
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('workshop_update_request:header', 'Update request')}
      data-test-id="update-requests-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w="full">
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setStatus(initialFilters?.status);
              setAssignee(initialFilters?.assignee);
              setItemsPerPage(initialFilters?.itemsPerPage);
              window.localStorage.setItem('workshop_update_requests_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="workshop-update-request-table"
            data={workshopUpdateRequestsQuery.data?.data || []}
            globalFilter={searchString}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={true}
            filtersSelected={filtersSelected}
            globalFilterPlaceholder={t('workshop_update_request:search')}
            hasSearchInput={true}
            // @ts-ignore
            hasFilters={true}
            // @ts-ignore
            currentPage={workshopUpdateRequestsQuery.data?.current_page - 1}
            perPage={itemsPerPage}
            // @ts-ignore
            total={workshopUpdateRequestsQuery.data?.last_page}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={workshopUpdateRequestsQuery.isFetching || workshopUpdateRequestsQuery.isLoading}
            // @ts-ignore
            isMoreThenOnePage={
              !initialLoad ||
              Boolean(workshopUpdateRequestsQuery.data?.next_page_url) ||
              // @ts-ignore
              workshopUpdateRequestsQuery.data?.current_page > 0
            }
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WorkshopUpdateRequestOverviewCard;
